/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  GenericFinderDialogConfig,
  whenFormInvalid,
} from '@gh/builder-shared';
import { ActionFlow, getAllNewSelectedEntities } from '@gh/common-shared';
import { AutocompleteItem, getAutocompleteItemId, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { first } from 'lodash';
import {
  complementReportRohcOffice,
  getReportRohcAreaId,
  getReportRohcMasterCcId,
  getReportRohcOfficeAreaId,
  getReportRohcOfficeCatalogId,
  getReportRohcOfficeCcId,
  getReportRohcOfficeId,
  newReportRohcArea,
  newReportRohcMasterCc,
  ReportRohcArea,
  ReportRohcDetails,
  ReportRohcDetailsTab,
  ReportRohcMasterCc,
  ReportRohcOffice,
  ReportRohcOfficeCc,
} from '../../resources/report-roch-details.model';
import {
  ReportRohcDetailsBusinessService,
  ReportRohcOfficeCcFinderDialogConfig,
  reportRohcOfficeCcFinderDialogRoute,
  reportRohcOfficeFinderDialogRoute,
} from '../../resources/report-rohc-details-business.service';
import { reportRohcAreaDialogRoute } from './report-rohc-area-dialog/report-rohc-area-dialog.component';
import { ReportRohcDetailsFormState } from './report-rohc-details-form.service';
import { reportRohcMasterCcDialogRoute } from './report-rohc-master-cc-dialog/report-rohc-master-cc-dialog.component';

@Injectable()
export class ReportRohcDetailsCommandService {
  constructor(private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private notifications: NotificationService,
              private translations: TranslationService,
              private dialogs: DialogService,
              private waitings: WaitingService,
              private business: ReportRohcDetailsBusinessService,
              private form: ReportRohcDetailsFormState) {

  }

  get completeFlow(): ActionFlow<FormFlowState<ReportRohcDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) => this.business.update(value)),
      finish: ({ value }) => {
        this.form.build(value);
      },
    });
  }

  build(details: ReportRohcDetails): void {
    this.form.build(details);
  }

  reset(): void {
    this.dialogs
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.get().pipe(this.waitings.operator())))
      .subscribe((details) => {
        this.form.build(details);
      });
  }

  selectTab(tab: ReportRohcDetailsTab): void {
    this.form.selectTab(tab);
  }

  createArea(): void {
    this.dialogs.openWithInput(reportRohcAreaDialogRoute, {
      isCreateMode: true,
      area: newReportRohcArea(),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((area: any) => {
        this.form.addArea(area);
        this.form.setSelectedAreaId(getReportRohcAreaId(area));
      });
  }

  openArea(area: ReportRohcArea): void {
    this.dialogs.openWithInput(reportRohcAreaDialogRoute, {
      isCreateMode: false,
      area,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedArea: any) => this.form.updateArea(updatedArea));
  }

  deleteArea(area: ReportRohcArea): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_area_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeArea(area));
  }

  selectArea(id: ID): void {
    this.form.setSelectedAreaId(id);
  }

  resetOfficeFocusedCell(): void {
    this.form.resetOfficeFocusedCell();
  }

  addOffice(): void {
    const areaId = this.form.selectedAreaId!; // tslint:disable-line:no-non-null-assertion

    this.dialogs.openWithInput(reportRohcOfficeFinderDialogRoute, <GenericFinderDialogConfig>{
      preselected: this.form.selectedOffices.map(getReportRohcOfficeCatalogId),
      multiselection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((offices: ReportRohcOffice[]) =>
        this.form.addOffices(offices.map((office) => ({ ...office, areaId })).map(complementReportRohcOffice)));
  }

  removeOffice(office: ReportRohcOffice): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_delete_office_from_area')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeOffices([office]));
  }

  setOfficeHousePlan(entity: ReportRohcOffice, housePlan?: AutocompleteItem): void {
    this.form.setOfficeHousePlan(entity, housePlan);
  }

  createMasterCc(): void {
    this.dialogs.openWithInput(reportRohcMasterCcDialogRoute, {
      isCreateMode: true,
      masterCc: newReportRohcMasterCc(),
      isCodeUnique: (code: number) => this.form.isCodeUnique(code),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((masterCc: any) => {
        this.form.addMasterCc(masterCc);
        this.form.setSelectedMasterCcId(getReportRohcMasterCcId(masterCc));
      });
  }

  openMasterCc(masterCc: ReportRohcMasterCc): void {
    this.dialogs.openWithInput(reportRohcMasterCcDialogRoute, {
      isCreateMode: false,
      masterCc,
      isCodeUnique: (code: number) => this.form.isCodeUnique(code),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updateMasterCc: any) => this.form.updateMasterCc(updateMasterCc));
  }

  deleteMasterCc(masterCc: ReportRohcMasterCc): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_master_cc_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.removeMasterCc(masterCc));
  }

  selectMasterCc(id: ID): void {
    this.form.setSelectedMasterCcId(id);
  }

  addOfficeCc(): void {
    const masterCcId = this.form.selectedMasterCcId!; // tslint:disable-line:no-non-null-assertion

    this.dialogs.openWithInput(reportRohcOfficeCcFinderDialogRoute, <ReportRohcOfficeCcFinderDialogConfig>{
      preselected: this.form.selectedOfficeCostCenters.map(getReportRohcOfficeCcId),
      multiselection: true,
      filter: {
        housePlanIds: this.form.selectedHousePlans.map(getAutocompleteItemId),
      },
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenters: ReportRohcOfficeCc[]) =>
        this.form.addOfficeCcs(costCenters.map((cc) => ({ ...cc, masterId: masterCcId }))));
  }

  removeOfficeCc(officeCc: ReportRohcOfficeCc): void {
    this.form.removeOfficeCc([officeCc]);
  }

  setGridLayout(name: string, layout: GridLayout): void {
    this.form.setGridLayout(name, layout);
  }

  private handleErrors(): void {
    const invalidOffice = first(this.form.invalidAreaOffices);
    if (invalidOffice) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
      this.form.selectTab(ReportRohcDetailsTab.Areas);
      this.form.setSelectedAreaId(getReportRohcOfficeAreaId(invalidOffice));
      this.form.setOfficeFocusedCell({ id: getReportRohcOfficeId(invalidOffice), field: 'housePlan' });
    }
  }
}
